import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import Select from 'react-select';
import leven from 'leven';
import './css/BulkUpload.css';

function debounce(func, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    return new Promise(resolve => {
      timeout = setTimeout(() => resolve(func(...args)), wait);
    });
  };
}

const questionHeaders = Array.from({ length: 36 }, (_, i) => `question${i + 1}`);

const sampleTemplates = {
  leaders: [['name', 'email'], ['Adam Taunton', 'ataunton@topical.com']],
  raters: [
    ['rateremail', 'ratername', 'relationship'],
    ['rater1@example.com', 'Rater One', 'Peer'],
  ],
  'rater-responses': [
    ['subjectemail', 'respondentemail', ...questionHeaders, 'strengths', 'dodifferently', 'additionalcomments'],
    ['leader1@example.com', 'rater1@example.com', ...Array(36).fill('4'), 'Good with customers', 'Be more proactive', 'Great team player'],
  ],
  'constant-contact': [
    [
      'First Name',
      'Last Name',
      'Email Address',
      'Respondent #',
      'Date/Time',
      '1. Please enter YOUR email address here',
      '2. Please enter the email address of the PERSON YOU ARE PROVIDING FEEDBACK FOR',
      '3. First, please tell us your relationship to the person being rated by selecting from the choices below.',
      '4. This individual actively seeks to learn new skills to keep current with changing times.',
      '5. This individual recognizes when he or she has a skill gap and pursues training to address it.',
      '6. This individual collaborates and networks to learn new approaches.',
      '7. This individual is aware of or is open to suggestions about his or her own personal limitations.',
      '8. This individual considers points of view other then his or her own.',
      '9. This person understands the role he or she plays on the team in various situations.',
      '10. This individual is aware of his or her own unique strengths and uses them effectively to accomplish tasks at hand.',
      '11. This person uses feedback to know when he or she is over using a strength.',
      '12. This individual recognizes and leverages the strengths of others as appropriate.',
      '13. This individual establishes a vision and mission, and sets the bar high for everyone involved.',
      '14. This individual introduces new challenges for the team with a clear vision and mission.',
      '15. This person behaves in a manner that is consistent with the vision and mission.',
      '16. This individual is welcoming and approachable.',
      '17. This individual is quick to give others recognition when deserved.',
      '18. This individual listens effectively while encouraging people to share thoughts.',
      '19. This individual presents a plan that is clear, concise and achievable.',
      '20. His or her plans provide the actions that are needed to achieve the goal or objective.',
      '21. This individual creates plans with clearly defined roles and responsibilities.',
      '22. This individual holds him or herself accountable as necessary and appropriate.',
      '23. This individual holds others accountable when necessary and appropriate.',
      '24. This individual takes responsibility and promotes others to do the same.',
      '25. Shows empathy and listens to others during stressful situations involving conflict.',
      '26. This person challenges the group to address issues when there are strong disagreements about approach.',
      '27. This individual promotes opportunities for improvement when teams are struggling to find common ground.',
      '28. This individual influences others while being respectful, positive and enthusiastic.',
      '29. This individual presents his or herself confidently.',
      '30. This individual provides a calming influence in a heated situation.',
      '31. This person networks and reaches out to others beyond the immediate group.',
      '32. This person is open and authentic.',
      '33. This person helps foster a mutual respect.',
      '34. This individual puts forth extra effort to seek out input from others to improve his or herself.',
      '35. This individual seeks feedback from others to improve products, processes or procedures.',
      '36. This person encourages others to seek feedback as a part of their development.',
      '37. This individual effectively inspires others to stretch beyond their comfort zones.',
      '38. This person presents new ideas in a way that energizes and excites.',
      '39. Individuals feel valued and inspired by this person.',
      '40. What do you view as this person\'s top 2-3 strengths?',
      '41. What is one thing you\'d like to see this person do differently?',
      '42. Anything else you want to bring to this person\'s attention?',
    ],
    [
      '',
      '',
      'Anonymous',
      '1',
      '11/04/2024 12:48:25 PM EST',
      'dharmesh.rajyaguru@aexp.com',
      'Subramaniam.Ramanathan2@aexp.com',
      'Peer',
      '5',
      '5',
      '5',
      '5',
      '5',
      '5',
      '5',
      '1',
      '3',
      '5',
      '5',
      '5',
      '5',
      '3',
      '4',
      '5',
      '5',
      '5',
      '5',
      '5',
      '1',
      '3',
      '5',
      '3',
      '3',
      '5',
      '3',
      '5',
      '5',
      '3',
      '3',
      '3',
      '5',
      '5',
      '5',
      '3',
      'Subbu stays on top of his work and team\'s tasks.',
      'Avoid micro management',
      'Be friends with team members.',
    ],
  ],
};

const previewHeaders = {
  leaders: ['name', 'email', 'product'],
  raters: ['rateremail', 'ratername', 'relationship', 'product'],
  'rater-responses': [
    'subjectemail',
    'respondentemail',
    'product',
    'questions',
    'strengths',
    'dodifferently',
    'additionalcomments',
  ],
  'constant-contact': [
    'subjectemail',
    'respondentemail',
    'product',
    'questions',
    'strengths',
    'dodifferently',
    'additionalcomments',
  ],
};

const BulkUploadComponent = () => {
  const [uploadType, setUploadType] = useState('');
  const [csvFormat, setCsvFormat] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [products, setProducts] = useState([]);
  const [csvData, setCsvData] = useState(() => {
    const savedCsvData = localStorage.getItem('bulkUploadCsvData');
    return savedCsvData ? JSON.parse(savedCsvData) : [];
  });
  const [pastedCSV, setPastedCSV] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [previewData, setPreviewData] = useState(() => {
    const savedPreviewData = localStorage.getItem('bulkUploadPreviewData');
    return savedPreviewData ? JSON.parse(savedPreviewData) : null;
  });
  const [invalidRows, setInvalidRows] = useState([]);
  const [leaderEmails, setLeaderEmails] = useState([]);
  const [raterEmails, setRaterEmails] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [leaderRaterMap, setLeaderRaterMap] = useState({});
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [fileFormat, setFileFormat] = useState('csv');
  const [leaders, setLeaders] = useState([]);
  const [selectedLeader, setSelectedLeader] = useState('');
  const [selfWarning, setSelfWarning] = useState('');

  // **Sanitize String Function**
  const sanitizeString = (str) => {
    if (typeof str === 'string') {
      return str
        .replace(/\\/g, '\\\\') // Escape backslashes
        .replace(/"/g, '\\"');  // Escape double quotes
    }
    return str;
  };

  // **Persist Upload Type and CSV Format to Local Storage**
  useEffect(() => {
    localStorage.setItem('bulkUploadUploadType', uploadType);
    localStorage.setItem('bulkUploadCsvFormat', csvFormat);
  }, [uploadType, csvFormat]);

  // **Load Saved States on Mount**
  useEffect(() => {
    const savedUploadType = localStorage.getItem('bulkUploadUploadType');
    const savedCsvFormat = localStorage.getItem('bulkUploadCsvFormat');
    if (savedUploadType) setUploadType(savedUploadType);
    if (savedCsvFormat) setCsvFormat(savedCsvFormat);
  }, []);

  // **Utility Functions**
  const isLikelyName = (input) => !input.includes('@');

  const findClosestNameMatch = (inputName, leaderData) => {
    const normalizedInput = inputName.toLowerCase().trim().replace(/[^a-zA-Z ]/g, '').replace(/\s+/g, ' ');
    let minDistance = Infinity;
    let closestLeader = null;

    leaderData.forEach(leader => {
      const distance1 = leven(normalizedInput, leader.name1);
      const distance2 = leven(normalizedInput, leader.name2);
      const distance = Math.min(distance1, distance2);
      if (distance < minDistance) {
        minDistance = distance;
        closestLeader = leader;
      }
    });

    return minDistance <= 2 ? closestLeader.email : null;
  };

  const findClosestMatch = (email, existingEmails) => {
    const lowerEmail = email.toLowerCase();
    let minDistance = Infinity;
    let closestMatch = null;

    existingEmails.forEach(existing => {
      const lowerExisting = existing.toLowerCase();
      const distance = leven(lowerEmail, lowerExisting);
      if (distance < minDistance && lowerExisting !== lowerEmail) {
        minDistance = distance;
        closestMatch = existing;
      }
    });

    return minDistance <= 2 ? closestMatch : null;
  };

  // **Fetch Existing Emails with Leader-Rater Mapping**
  const fetchExistingEmails = async () => {
    try {
      const response = await axios.get('/api/existing-emails', { params: { product: selectedProduct } });
      const { leaderData, leaderEmails, raterEmails, leaderRaterMap } = response.data;

      setLeaderEmails(leaderEmails);
      setRaterEmails(raterEmails);
      setLeaderRaterMap(leaderRaterMap);

      const leaderEmailSet = new Set(leaderEmails.map(email => email.toLowerCase()));
      const raterEmailSet = new Set(raterEmails.map(email => email.toLowerCase()));
      const newSuggestions = {};

      csvData.forEach((row, index) => {
        const lowerSubjectEmail = row.subjectemail.toLowerCase().trim();
        const lowerRespondentEmail = row.respondentemail.toLowerCase().trim();

        if (isLikelyName(row.subjectemail)) {
          const nameSuggestion = findClosestNameMatch(row.subjectemail, leaderData);
          if (nameSuggestion) newSuggestions[`subjectemail-${index}`] = nameSuggestion;
        } else if (!leaderEmailSet.has(lowerSubjectEmail)) {
          const subjectSuggestion = findClosestMatch(row.subjectemail, leaderEmails);
          if (subjectSuggestion && subjectSuggestion.toLowerCase() !== lowerSubjectEmail) {
            newSuggestions[`subjectemail-${index}`] = subjectSuggestion;
          }
        }

        if (!raterEmailSet.has(lowerRespondentEmail)) {
          const respondentSuggestion = findClosestMatch(row.respondentemail, raterEmails);
          if (respondentSuggestion && respondentSuggestion.toLowerCase() !== lowerRespondentEmail) {
            newSuggestions[`respondentemail-${index}`] = respondentSuggestion;
          }
        }
      });

      setSuggestions(newSuggestions);
    } catch (error) {
      console.error('Error fetching existing emails:', error);
      setStatusMessage('Failed to load email suggestions.');
    }
  };

  useEffect(() => {
    if (uploadType === 'rater-responses' && selectedProduct && csvData.length > 0) {
      fetchExistingEmails();
    }
  }, [csvData, selectedProduct]);

  // **Fetch Products**
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get('/api/products');
        setProducts(response.data.map(p => ({ id: p._id, name: p.productName })));
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  // **Fetch Leaders for Raters Upload**
  useEffect(() => {
    if (uploadType === 'raters' && selectedProduct) {
      const fetchLeaders = async () => {
        try {
          const response = await axios.get(`/api/leaders?product=${selectedProduct}`);
          setLeaders(response.data.map((l) => ({ value: l._id, label: l.email })));
        } catch (error) {
          console.error('Error fetching leaders:', error);
          setStatusMessage('Failed to load leaders.');
        }
      };
      fetchLeaders();
    } else {
      setLeaders([]);
      setSelectedLeader('');
    }
  }, [uploadType, selectedProduct]);

  // **Populate Sample Data**
  const populateSampleData = () => {
    if (uploadType) {
      const templateKey = uploadType === 'rater-responses' && csvFormat === 'constant-contact' ? 'constant-contact' : uploadType;
      const sampleData = sampleTemplates[templateKey];
      if (sampleData) {
        const delimiter = fileFormat === 'csv' ? ',' : '\t';
        const csvString = Papa.unparse(sampleData, { delimiter });
        setPastedCSV(csvString);
      } else {
        setPastedCSV('');
      }
    } else {
      setPastedCSV('');
    }
  };

  useEffect(() => {
    populateSampleData();
  }, [uploadType, csvFormat, fileFormat]);

  // **Handle File Drop**
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        setStatusMessage('No file selected.');
        return;
      }
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target.result) {
          processCSV(event.target.result);
        } else {
          setStatusMessage('Failed to read the file.');
        }
      };
      reader.onerror = () => setStatusMessage('Error reading the file.');
      reader.readAsText(file);
    },
    [csvFormat, selectedProduct, selectedLeader, uploadType, fileFormat]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const normalizeHeader = (header) => header.toLowerCase().trim();

  // **Process CSV Data**
  const processCSV = (text) => {
    setStatusMessage('Processing file...');
    try {
      if (!text || typeof text !== 'string') {
        throw new Error('Invalid file content.');
      }
      const delimiter = fileFormat === 'csv' ? ',' : '\t';
      const result = Papa.parse(text, {
        header: false,
        skipEmptyLines: true,
        delimiter,
        quoteChar: '"',
        escapeChar: '"',
      });
      if (result.errors.length > 0) {
        throw new Error(`Parsing errors: ${result.errors.map((e) => e.message).join(', ')}`);
      }
      const rows = result.data;
      if (rows.length < 2) {
        throw new Error('File is empty or lacks data rows after headers.');
      }
      const actualHeaders = rows[0].map((header) => normalizeHeader(header));
      let expected;
      if (uploadType === 'leaders') {
        expected = ['name', 'email'];
      } else if (uploadType === 'raters') {
        expected = ['rateremail', 'ratername', 'relationship'];
      } else if (uploadType === 'rater-responses') {
        if (csvFormat === 'standard') {
          expected = ['subjectemail', 'respondentemail', ...questionHeaders, 'strengths', 'dodifferently', 'additionalcomments'];
        } else if (csvFormat === 'constant-contact') {
          expected = sampleTemplates['constant-contact'][0];
        }
      }
      if (!expected) {
        setStatusMessage('Invalid upload type or format selected.');
        setCsvData([]);
        setPreviewData(null);
        return;
      }
      const expectedNormalized = expected.map(normalizeHeader);
      if (uploadType === 'rater-responses' && csvFormat === 'constant-contact') {
        if (
          actualHeaders.length !== expectedNormalized.length ||
          !actualHeaders.every((header, index) => header === expectedNormalized[index])
        ) {
          setStatusMessage(
            `Error: Invalid or missing headers for Constant Contact format. Expected exactly 47 columns with headers: ${expected.join(', ')}.`
          );
          setCsvData([]);
          setPreviewData(null);
          return;
        }
      } else {
        const actualSet = new Set(actualHeaders);
        const expectedSet = new Set(expectedNormalized);
        if (
          actualSet.size !== expectedSet.size ||
          ![...expectedSet].every((header) => actualSet.has(header))
        ) {
          setStatusMessage(
            `Error: Invalid or missing headers. Expected exactly: ${expected.join(', ')}.`
          );
          setCsvData([]);
          setPreviewData(null);
          return;
        }
      }
      if (uploadType === 'raters') {
        if (!selectedProduct || !selectedLeader) {
          setStatusMessage('Please select both a product and a leader for raters upload.');
          setCsvData([]);
          setPreviewData(null);
          return;
        }
        const headers = actualHeaders;
        const dataRows = rows.slice(1).map((cells) => {
          const row = {};
          headers.forEach((header, i) => (row[header] = cells[i] || ''));
          row.product = selectedProduct;
          row.leaderId = selectedLeader;
          return row;
        });
        setCsvData(dataRows);
        setPreviewData(dataRows);
        setStatusMessage(`Processed ${dataRows.length} rows.`);
        checkSelfRater(dataRows);
      } else if (uploadType === 'rater-responses') {
        if (!selectedProduct) {
          setStatusMessage('Please select a product for rater responses upload.');
          setCsvData([]);
          setPreviewData(null);
          return;
        }
        if (csvFormat === 'constant-contact') {
          const dataRows = rows.slice(1).map((cells, index) => {
            if (cells.length !== 47) {
              throw new Error(`Row ${index + 2} has ${cells.length} columns, expected 47.`);
            }
            const questions = cells.slice(8, 44).map((q, qIdx) => {
              const num = q ? Number(q) : 0;
              if (isNaN(num) || num < 1 || num > 6) {
                throw new Error(
                  `Row ${index + 2}, Question ${qIdx + 1}: Invalid rating "${q}". Must be a number between 1 and 6.`
                );
              }
              return num;
            });
            return {
              subjectemail: cells[6]?.trim() || '',
              respondentemail: cells[5]?.trim() || '',
              product: selectedProduct,
              questions: questions.join(','),
              strengths: cells[44] || '',
              dodifferently: cells[45] || '',
              additionalcomments: cells[46] || '',
            };
          });
          setCsvData(dataRows);
          setPreviewData(dataRows);
          setStatusMessage(
            `Processed ${dataRows.length} rows from Constant Contact ${fileFormat.toUpperCase()}. Validating rows...`
          );
          validateRaters(dataRows);
        } else {
          const headers = actualHeaders;
          const dataRows = rows
            .slice(1)
            .filter((cells) => {
              return (
                cells[0]?.toLowerCase() !== headers[0] ||
                cells[1]?.toLowerCase() !== headers[1]
              );
            })
            .map((cells, index) => {
              if (cells.length !== headers.length) {
                throw new Error(`Row ${index + 2} has ${cells.length} columns, expected ${headers.length}.`);
              }
              const row = {};
              headers.forEach((header, i) => (row[header] = cells[i] || ''));
              const questions = questionHeaders.map((qHeader, qIdx) => {
                const q = row[qHeader];
                const num = q ? Number(q) : 0;
                if (isNaN(num) || num < 1 || num > 6) {
                  throw new Error(
                    `Row ${index + 2}, Question ${qIdx + 1}: Invalid rating "${q}". Must be a number between 1 and 6.`
                  );
                }
                return num;
              });
              return {
                subjectemail: row.subjectemail,
                respondentemail: row.respondentemail,
                product: selectedProduct,
                questions: questions.join(','),
                strengths: row.strengths,
                dodifferently: row.dodifferently,
                additionalcomments: row.additionalcomments,
              };
            });
          setCsvData(dataRows);
          setPreviewData(dataRows);
          setStatusMessage(`Processed ${dataRows.length} rows. Validating rows...`);
          validateRaters(dataRows);
        }
      } else {
        if (!selectedProduct) {
          setStatusMessage('Please select a product for this upload.');
          setCsvData([]);
          setPreviewData(null);
          return;
        }
        const headers = actualHeaders;
        const dataRows = rows.slice(1).map((cells) => {
          const row = {};
          headers.forEach((header, i) => (row[header] = cells[i] || ''));
          row.product = selectedProduct;
          return row;
        });
        setCsvData(dataRows);
        setPreviewData(dataRows);
        setStatusMessage(`Processed ${dataRows.length} rows.`);
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setStatusMessage(`Error processing ${fileFormat.toUpperCase()}: ${error.message}`);
      setCsvData([]);
      setPreviewData(null);
    }
  };

  // **Check for "Self" Rater in Raters Upload**
  const checkSelfRater = (dataRows) => {
    const selfRater = dataRows.find(row => row.relationship.toLowerCase().trim() === 'self');
    if (!selfRater) {
      setSelfWarning('Warning: No rater with relationship "Self" found. This might be okay, but please double-check your data.');
    } else {
      const leaderEmail = leaders.find(l => l.value === selectedLeader)?.label.toLowerCase().trim();
      const selfEmail = selfRater.rateremail.toLowerCase().trim();
      if (selfEmail !== leaderEmail) {
        setSelfWarning(`Warning: The email for "Self" rater (${selfEmail}) does not match the selected leader's email (${leaderEmail}). Please verify.`);
      } else {
        setSelfWarning('');
      }
    }
  };

  // **Validate Rater Responses**
  const validateRaters = async (data) => {
    try {
      setIsValidating(true);
      const validationData = data.map((row) => {
        const questionCount = row.questions.split(',').length;
        if (questionCount !== 36) {
          throw new Error(`Row with subjectemail ${row.subjectemail} has ${questionCount} questions, expected 36.`);
        }
        return {
          subjectEmail: row.subjectemail,
          respondentEmail: row.respondentemail,
          product: row.product,
        };
      });
      const response = await axios.post('/api/validate-raters', { data: validationData });
      const { invalidRows } = response.data;
      setInvalidRows(invalidRows);
      setIsValidating(false);
      if (invalidRows.length > 0) {
        setStatusMessage(`Validation complete: ${invalidRows.length} invalid rows found.`);
      } else {
        setStatusMessage('Validation complete: All rows are valid. You can now upload the data.');
      }
    } catch (error) {
      console.error('Error validating rows:', error);
      setStatusMessage('Failed to validate rows. Please try again.');
      setInvalidRows([]);
      setIsValidating(false);
    }
  };

  const handlePasteCSV = () => {
    if (!pastedCSV.trim()) {
      setStatusMessage('No pasted data to process.');
      return;
    }
    processCSV(pastedCSV);
  };

  // **Handle Data Upload**
  const handleUpload = async () => {
    if (csvData.length === 0) {
      setStatusMessage('No data to upload. Please process a file first.');
      return;
    }
    if (!uploadType) {
      setStatusMessage('Please select an upload type.');
      return;
    }
    if (!selectedProduct) {
      setStatusMessage('Please select a product for the upload.');
      return;
    }
    if (uploadType === 'raters' && !selectedLeader) {
      setStatusMessage('Please select a leader for the raters upload.');
      return;
    }
    if (uploadType === 'rater-responses' && invalidRows.length > 0) {
      setStatusMessage(
        `Upload failed: ${invalidRows.length} invalid rows found. Correct or remove the highlighted rows and re-validate.`
      );
      return;
    }
    setIsLoading(true);
    setStatusMessage('Uploading data...');
    try {
      const sanitizedData = csvData.map(row => {
        const sanitizedRow = {};
        for (const key in row) {
          sanitizedRow[key] = sanitizeString(row[key]);
        }
        return sanitizedRow;
      });
      const payload = { data: sanitizedData };
      const response = await axios.post(`/api/bulk-upload/${uploadType}`, payload, {
        withCredentials: true,
      });

      let successMessage = '';
      if (uploadType === 'leaders') {
        successMessage = 'Upload successful for leaders';
      } else if (uploadType === 'raters') {
        const leaderEmail = leaders.find(l => l.value === selectedLeader)?.label || 'selected leader';
        successMessage = `Upload successful for ${leaderEmail}`;
      } else if (uploadType === 'rater-responses') {
        successMessage = 'Upload successful for rater responses';
      }

      setStatusMessage(`${successMessage}. Sample data has been reloaded for your reference.`);
      localStorage.removeItem('bulkUploadCsvData');
      localStorage.removeItem('bulkUploadPreviewData');
      setCsvData([]);
      setPreviewData(null);
      setInvalidRows([]);
      setSelfWarning('');
      populateSampleData();
    } catch (error) {
      console.error('Error during upload:', error);
      const errorData = error.response?.data;
      if (errorData && errorData.message) {
        setStatusMessage(`Upload failed: ${errorData.message}\nDetails: ${JSON.stringify(errorData.invalidRows || {}, null, 2)}`);
      } else {
        setStatusMessage('Upload failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const debounceValidate = useCallback(
    debounce((data) => validateRaters(data), 500),
    []
  );

  // **Handle Email Correction**
  const handleEmailChange = (index, header, value) => {
    const newCsvData = [...csvData];
    newCsvData[index][header] = value;
    setCsvData(newCsvData);
    setPreviewData(newCsvData);
    setIsValidating(true);
    debounceValidate(newCsvData).then(() => setIsValidating(false));
  };

  // **Accept Suggestion**
  const handleSuggestionAccept = (index, header, suggestion) => {
    handleEmailChange(index, header, suggestion);
    setSuggestions(prev => {
      const newSuggestions = { ...prev };
      delete newSuggestions[`${header}-${index}`];
      return newSuggestions;
    });
  };

  // **Remove Invalid Row**
  const handleRemoveRow = (index) => {
    if (window.confirm('Are you sure you want to remove this row? It will not be included in the upload.')) {
      const newCsvData = csvData.filter((_, i) => i !== index);
      setCsvData(newCsvData);
      setPreviewData(newCsvData);
      validateRaters(newCsvData);
    }
  };

  const openCorrectionModal = (index) => {
    setCurrentIndex(index);
    setCorrectionModalOpen(true);
  };

  const closeCorrectionModal = () => {
    setCorrectionModalOpen(false);
    setCurrentIndex(null);
  };

  // **Download Sample File**
  const downloadSampleCSV = () => {
    if (!uploadType) {
      setStatusMessage('Select an upload type to download a sample.');
      return;
    }
    const templateKey = uploadType === 'rater-responses' && csvFormat === 'constant-contact' ? 'constant-contact' : uploadType;
    const sampleData = sampleTemplates[templateKey] || [];
    const delimiter = fileFormat === 'csv' ? ',' : '\t';
    const csvContent = Papa.unparse(sampleData, { delimiter });
    const blob = new Blob([csvContent], { type: `text/${fileFormat};charset=utf-8;` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${templateKey}-sample.${fileFormat}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const disabledUpload =
    isLoading || isValidating || !uploadType || (uploadType === 'rater-responses' && invalidRows.length > 0);

  const productOptions = products.map(product => ({
    value: product.id,
    label: product.name
  }));

  const leaderOptions = leaders;

  const headersKey = uploadType === 'rater-responses' && csvFormat === 'constant-contact' ? 'constant-contact' : uploadType;

  // **Render Component**
  return (
    <div onClick={(e) => e.stopPropagation()} className="bulk-upload-container">
      <h2>Bulk Upload</h2>
      <div className="upload-type-selection">
        <label htmlFor="upload-type">Select Upload Type:</label>
        <select id="upload-type" value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
          <option value="">-- Choose Upload Type --</option>
          <option value="leaders">Leaders</option>
          <option value="raters">Raters</option>
          <option value="rater-responses">Rater Responses</option>
        </select>
      </div>
      {uploadType && (
        <div className="file-format-selection">
          <label htmlFor="file-format">File Format:</label>
          <select id="file-format" value={fileFormat} onChange={(e) => setFileFormat(e.target.value)}>
            <option value="csv">CSV (Comma-separated)</option>
            <option value="tsv">TSV (Tab-separated)</option>
          </select>
        </div>
      )}
      {(uploadType === 'leaders' || uploadType === 'raters' || uploadType === 'rater-responses') && (
        <div className="product-selection">
          <label htmlFor="product-select">Select Product:</label>
          <Select
            id="product-select"
            options={productOptions}
            value={productOptions.find((option) => option.value === selectedProduct) || null}
            onChange={(selected) => setSelectedProduct(selected ? String(selected.value) : '')}
            placeholder="Search for a product..."
            isSearchable
          />
        </div>
      )}
      {uploadType === 'rater-responses' && (
        <p className="info-message">The selected product will be applied to all rows in the upload.</p>
      )}
      {uploadType === 'raters' && (
        <div className="leader-selection">
          <label htmlFor="leader-select">Select Leader:</label>
          <Select
            id="leader-select"
            options={leaderOptions}
            value={leaderOptions.find((option) => option.value === selectedLeader) || null}
            onChange={(selected) => setSelectedLeader(selected ? selected.value : '')}
            placeholder="Search for a leader..."
            isSearchable
          />
        </div>
      )}
      {uploadType === 'rater-responses' && (
        <div className="warning-message">
          <p>
            <strong>Important:</strong> For rater responses, questions in the CSV must be numbers between 1 and 6.
          </p>
        </div>
      )}
      {uploadType === 'rater-responses' && (
        <div className="csv-format-selection">
          <label htmlFor="csv-format">CSV Format:</label>
          <select id="csv-format" value={csvFormat} onChange={(e) => setCsvFormat(e.target.value)}>
            <option value="standard">Standard</option>
            <option value="constant-contact">Constant Contact</option>
          </select>
        </div>
      )}
      {uploadType && (
        <div className="upload-options">
          <div className="file-upload" {...getRootProps()}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the file here...</p>
            ) : (
              <p>Drag 'n' drop a {fileFormat.toUpperCase()} file here, or click to select</p>
            )}
          </div>
          <p>OR</p>
          <div className="paste-upload">
            <textarea
              placeholder={
                uploadType === 'leaders'
                  ? `Paste ${fileFormat.toUpperCase()} here with headers: name, email`
                  : uploadType === 'raters'
                    ? `Paste ${fileFormat.toUpperCase()} here with headers: rateremail, ratername, relationship`
                    : uploadType === 'rater-responses' && csvFormat === 'standard'
                      ? `Paste ${fileFormat.toUpperCase()} here with headers: subjectemail, respondentemail, question1 to question36, strengths, dodifferently, additionalcomments`
                      : uploadType === 'rater-responses' && csvFormat === 'constant-contact'
                        ? `Paste Constant Contact ${fileFormat.toUpperCase()} here with exactly 47 columns and headers matching the sample`
                        : `Paste ${fileFormat.toUpperCase()} content here...`
              }
              value={pastedCSV}
              onChange={(e) => setPastedCSV(e.target.value)}
              rows={6}
            />
            <button onClick={handlePasteCSV}>Process Pasted {fileFormat.toUpperCase()}</button>
          </div>
        </div>
      )}
      {selfWarning && uploadType === 'raters' && (
        <div className="self-warning" style={{ color: '#d9534f', marginTop: '10px' }}>
          {selfWarning}
        </div>
      )}
      {previewData && headersKey && previewHeaders[headersKey] && (
        <div className="preview-section">
          <h3>Data Preview (All Rows)</h3>
          <div className="table-wrapper">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  {previewHeaders[headersKey].map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, index) => (
                  <tr
                    key={index}
                    style={{
                      backgroundColor:
                        uploadType === 'rater-responses' && invalidRows.some(r => r.index === index)
                          ? '#ffcccc'
                          : 'white',
                    }}
                  >
                    <td>
                      {uploadType === 'rater-responses' && invalidRows.some(r => r.index === index) && (
                        <>
                          <button onClick={() => openCorrectionModal(index)}>Correct</button>
                          <button onClick={() => handleRemoveRow(index)} style={{ marginLeft: '5px' }}>Remove</button>
                        </>
                      )}
                    </td>
                    {previewHeaders[headersKey].map((header) => (
                      <td key={header}>
                        {header === 'subjectemail' && suggestions[`subjectemail-${index}`] ? (
                          <div>
                            {row[header]}
                            <p>Suggestion: {suggestions[`subjectemail-${index}`]}</p>
                            <button onClick={() => handleSuggestionAccept(index, 'subjectemail', suggestions[`subjectemail-${index}`])}>
                              Accept
                            </button>
                          </div>
                        ) : header === 'respondentemail' && suggestions[`respondentemail-${index}`] ? (
                          <div>
                            {row[header]}
                            <p>Suggestion: {suggestions[`respondentemail-${index}`]}</p>
                            <button onClick={() => handleSuggestionAccept(index, 'respondentemail', suggestions[`respondentemail-${index}`])}>
                              Accept
                            </button>
                          </div>
                        ) : (
                          row[header]
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {uploadType === 'rater-responses' && invalidRows.length > 0 && (
            <div className="invalid-rows-section">
              <h3>Invalid Rows ({invalidRows.length})</h3>
              <p>Please correct or remove the following rows to proceed:</p>
              <ul>
                {invalidRows.map(({ index, reason }) => (
                  <li key={index}>
                    Row {index + 1}: {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {correctionModalOpen && (
        <div className="modal" style={{ position: 'fixed', top: '20%', left: '20%', background: 'white', padding: '20px', border: '1px solid #ccc' }}>
          <h3>Correct Row {currentIndex + 1}</h3>
          <p>Reason: {invalidRows.find(row => row.index === currentIndex)?.reason}</p>
          <label>Subject Email:</label>
          <Select
            options={leaderEmails.map(email => ({ value: email, label: email }))}
            value={{ value: csvData[currentIndex].subjectemail, label: csvData[currentIndex].subjectemail }}
            onChange={(selected) => handleEmailChange(currentIndex, 'subjectemail', selected.value)}
            placeholder="Select leader email"
            isSearchable
          />
          {suggestions[`subjectemail-${currentIndex}`] && (
            <p>
              Suggestion: {suggestions[`subjectemail-${currentIndex}`]}
              <button onClick={() => handleSuggestionAccept(currentIndex, 'subjectemail', suggestions[`subjectemail-${currentIndex}`])}>
                Accept
              </button>
            </p>
          )}
          <label>Respondent Email:</label>
          <Select
            options={
              leaderRaterMap[csvData[currentIndex].subjectemail.toLowerCase()]?.map(email => ({ value: email, label: email })) || raterEmails.map(email => ({ value: email, label: email }))
            }
            value={{ value: csvData[currentIndex].respondentemail, label: csvData[currentIndex].respondentemail }}
            onChange={(selected) => handleEmailChange(currentIndex, 'respondentemail', selected.value)}
            placeholder="Select rater email"
            isSearchable
          />
          {suggestions[`respondentemail-${currentIndex}`] && (
            <p>
              Suggestion: {suggestions[`respondentemail-${currentIndex}`]}
              <button onClick={() => handleSuggestionAccept(currentIndex, 'respondentemail', suggestions[`respondentemail-${currentIndex}`])}>
                Accept
              </button>
            </p>
          )}
          <button onClick={closeCorrectionModal}>Save and Close</button>
        </div>
      )}
      <div className="upload-actions">
        <button className="upload-button" onClick={handleUpload} disabled={disabledUpload}>
          {isLoading ? 'Uploading...' : 'Upload Data'}
        </button>
        <button className="sample-button" onClick={downloadSampleCSV} disabled={!uploadType}>
          Download Sample {fileFormat.toUpperCase()}
        </button>
      </div>
      {statusMessage && <div className="status-message">{statusMessage}</div>}
    </div>
  );
};

export default BulkUploadComponent;